"use client";

import { PublicBrandImage } from "./public-brand-image";

import { Check, ChevronDown, Search, X } from "lucide-react";
import { useRef, useState, type KeyboardEvent } from "react";

import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

import { useLaunchViewerSessionControls } from "@/components/virtualspace/launch-viewer-session-context";
import { useLaunchActiveModel } from "@/components/virtualspace/launch-active-model-context";
import { VirtualSpaceAttribution } from "@/components/virtualspace/virtual-space-attribution";
import ShareActions from "@/components/virtualspace/share-actions";
import SyncDocumentTitle from "@/components/virtualspace/sync-document-title";
import { cn } from "@/lib/utils";
import { formatVirtualSpaceShareTitle } from "@/lib/virtualspace/virtual-space-share-title";
import {
  captureMonthKey,
  captureMonthLabel,
  formatCaptureDate,
  sectionSpaceLinks,
} from "@/lib/virtualspace/spacelink-groups";

/**
 * Public Launch controls: authorized model filtering, session navigation, and
 * exact-view links on the existing VirtualSpace share URL. The responsive dialog
 * supplies keyboard/focus handling and uses a bottom sheet on phones.
 */
export function LaunchMenu({
  vsName,
  documentTitle,
  organizationName,
  organizationLogoUrl,
  customerLogoUrl,
  customerName,
  shareUrlHost,
  fallbackShareToken,
  projectPageUrl,
  parentProjectHref,
  privatePreview = false,
}: {
  vsName: string;
  /** Server-resolved absolute title for the initial model. When
   *  the customer switches siblings we rebuild from the active
   *  label so the tab tracks the on-page pill. */
  documentTitle?: string | null;
  organizationName?: string | null;
  organizationLogoUrl?: string | null;
  customerLogoUrl?: string | null;
  customerName?: string | null;
  /** Origin (e.g. `https://explore.virtualspace.xyz`) used to build
   *  the active sibling's canonical Share URL. Passed as data
   *  (not a function) so this client component can be hydrated
   *  from a server-component parent without crossing the React
   *  "functions can't be props" boundary. */
  shareUrlHost: string;
  /** Current page token, retained when sharing any authorized sibling. */
  fallbackShareToken: string | null;
  /** Operator preview contains no public token or sharing controls. */
  privatePreview?: boolean;
  /** Legacy parent link; new callers use the server-authorized parentProjectHref. */
  projectPageUrl?: string | null;
  /** Authorized parent supplied by the server, independent of model count. */
  parentProjectHref?: string | null;
}) {
  const ctx = useLaunchActiveModel();
  const siblings = ctx?.siblings ?? [];
  const activeDeliverableId = ctx?.activeDeliverableId ?? null;
  const publishedSession = useLaunchViewerSessionControls();
  // A previous model may publish until its effect cleans up. Never share its pose.
  const session = !ctx?.activeProviderModelId || publishedSession?.modelId === ctx.activeProviderModelId
    ? publishedSession : null;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const hasSiblings = siblings.length > 1;
  const activeSibling = activeDeliverableId
    ? siblings.find((s) => s.deliverableId === activeDeliverableId) ?? null
    : siblings.find((s) => s.isCurrent) ?? null;
  const currentLabel = activeSibling?.label ?? vsName;
  const positionText = hasSiblings && activeSibling
    ? `${siblings.indexOf(activeSibling) + 1} of ${siblings.length}`
    : null;
  // The token still controls access. Pose/model fragments only select a view
  // within the server-resolved model set; no raw provider URL is shared.
  function buildShareUrl(fragment: string | null = null) {
    if (privatePreview || !fallbackShareToken) return "";
    const params = new URLSearchParams(fragment?.slice(1) ?? "");
    // Keep the current link's presentation and access rules. A sibling's
    // legacy token may be an embed or restricted guest link, even when
    // this page is the complete customer tour. The server-resolved model
    // set already authorizes selection under this token.
    if (activeDeliverableId) params.set("m", activeDeliverableId);
    const suffix = params.toString();
    return `${shareUrlHost}/launch/${encodeURIComponent(fallbackShareToken)}${suffix ? `#${suffix}` : ""}`;
  }
  // Model identity resets clipboard state; camera movement must not dismiss a
  // selectable link or cancel feedback for a copy already in progress.
  const shareUrl = buildShareUrl();
  const currentShareUrl = () => buildShareUrl(session?.getShareFragment() ?? null);

  const shareTitle = organizationName
    ? `${organizationName} · ${currentLabel}`
    : currentLabel;

  // Tab title tracks the active model when the customer switches
  // siblings; otherwise keep the server-resolved place title
  // (location + address / seo_title) so first paint isn't a short
  // floor label.
  const initialSiblingId =
    siblings.find((s) => s.isCurrent)?.deliverableId ??
    siblings[0]?.deliverableId ??
    null;
  const switchedSibling =
    hasSiblings &&
    activeSibling &&
    initialSiblingId &&
    activeSibling.deliverableId !== initialSiblingId;
  const liveDocumentTitle = switchedSibling
    ? formatVirtualSpaceShareTitle({ displayName: currentLabel })
    : documentTitle?.trim() ||
      formatVirtualSpaceShareTitle({ displayName: currentLabel });

  // Only filter the siblings already authorized and supplied by the server.
  // A query also matches the capture month ("2024", "march 2025") so a
  // building documented over years can be narrowed to one visit.
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const filteredSiblings = siblings.filter((s) =>
    s.label.toLocaleLowerCase().includes(normalizedQuery) ||
    (s.capturedAt
      ? captureMonthLabel(captureMonthKey(s.capturedAt)).toLocaleLowerCase().includes(normalizedQuery)
      : false),
  );
  // Preserve canonical floor/name or curated order across capture months.
  // Legacy unmarked sets retain their existing date section behavior.
  const allSections = siblings.some((s) => s.menuOrder || s.customMenuOrder) ? null : sectionSpaceLinks(siblings);
  const sections = allSections ? sectionSpaceLinks(filteredSiblings) ?? [{ key: "results", label: "", members: filteredSiblings }] : null;
  function onOpenChange(next: boolean) {
    setOpen(next);
    if (!next) setQuery("");
  }
  function onSelect(id: string) {
    onOpenChange(false);
    ctx?.setActiveDeliverableId(id);
  }
  function navigateResults(event: KeyboardEvent<HTMLElement>, fromSearch = false) {
    const buttons = Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>("button") ?? []);
    if (!buttons.length) return;
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    let next: number;
    if (event.key === "ArrowDown") next = (index + 1) % buttons.length;
    else if (event.key === "ArrowUp") next = index < 0 ? buttons.length - 1 : (index - 1 + buttons.length) % buttons.length;
    else if (!fromSearch && event.key === "Home") next = 0;
    else if (!fromSearch && event.key === "End") next = buttons.length - 1;
    else return;
    event.preventDefault();
    buttons[next]?.focus();
  }

  function renderSibling(s: (typeof siblings)[number]) {
    const isActive = s.deliverableId === activeSibling?.deliverableId;
    const dateText = allSections ? formatCaptureDate(s.capturedAt) : null;
    return (
      <li key={s.deliverableId}>
        <button
          type="button"
          aria-label={dateText ? `${s.label}, ${dateText}` : s.label}
          aria-current={isActive ? "true" : undefined}
          onClick={() => onSelect(s.deliverableId)}
          className={cn(
            "w-full min-h-11 flex items-center justify-between gap-2 px-4 py-2 text-left transition-colors motion-reduce:transition-none focus-visible:outline-2 focus-visible:outline-inset focus-visible:outline-white touch-manipulation",
            isActive
              ? "bg-white/10 text-white"
              : "text-white/85 hover:bg-white/8 hover:text-white",
          )}
        >
          <span className="inline-flex items-center gap-2 min-w-0">
            <span
              className={cn(
                "text-[9.5px] tabular-nums shrink-0 w-5 text-right",
                isActive ? "text-white/70" : "text-white/45",
              )}
            >
              {siblings.indexOf(s) + 1}
            </span>
            <span className="min-w-0">
              <span className="block text-sm break-words">{s.label}</span>
              {dateText ? (
                <span className="block text-[11px] tabular-nums text-white/55">{dateText}</span>
              ) : null}
            </span>
          </span>
          {isActive ? (
            <Check className="h-3.5 w-3.5 text-white shrink-0" />
          ) : null}
        </button>
      </li>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <div className="absolute top-[max(1rem,env(safe-area-inset-top))] right-[max(1rem,env(safe-area-inset-right))] z-50 max-w-[calc(100%-2rem-env(safe-area-inset-left)-env(safe-area-inset-right))]">
        <SyncDocumentTitle title={liveDocumentTitle} />
        <div className="inline-flex max-w-full items-center gap-1 rounded-2xl border border-white/15 bg-black/90 py-0.5 pl-3 pr-0.5 shadow-sm shadow-black/15 backdrop-blur-xl">
          <VirtualSpaceAttribution size={20} side="bottom" align="end" className="w-6 shrink-0 text-white" />
          <DialogTrigger asChild>
            <button
              ref={triggerRef}
              type="button"
              aria-label={hasSiblings ? "Choose floor or model" : "Tour options"}
              className="inline-flex min-h-11 min-w-11 max-w-[240px] flex-1 items-center gap-2 rounded-xl px-2 py-1 text-left text-white transition-colors hover:bg-white/10 active:bg-white/15 focus-visible:outline-2 focus-visible:outline-white motion-reduce:transition-none touch-manipulation sm:max-w-[320px]"
            >
              <span className="min-w-0 flex-1">
                {vsName !== currentLabel ? <span className="hidden truncate text-sm font-semibold leading-5 sm:block">{vsName}</span> : null}
                <span className="flex min-w-0 items-center gap-1 text-xs font-medium leading-5">
                  <span className="truncate">{currentLabel}</span>
                  {positionText ? <span className="shrink-0 text-[10px] tabular-nums text-white/65">· {positionText}</span> : null}
                </span>
              </span>
              <ChevronDown
                className={cn("size-3.5 shrink-0 text-white/70 transition-transform motion-reduce:transition-none", open && "rotate-180")}
                aria-hidden="true"
              />
            </button>
          </DialogTrigger>
          {!privatePreview && fallbackShareToken ? <ShareActions url={shareUrl} getUrl={currentShareUrl} title={shareTitle} iconOnly touchTarget preferProvidedUrl resilientClipboard consolidated /> : null}
        </div>
        {session?.status && <p role="status" className="mt-2 max-w-sm rounded-2xl border border-white/15 bg-black/90 px-3 py-2 text-xs text-white">{session.status}</p>}
      </div>
      <DialogContent
        onOpenAutoFocus={(event) => { if (hasSiblings) { event.preventDefault(); searchRef.current?.focus(); } }}
        className="left-0 right-0 top-auto bottom-0 flex max-h-[calc(100dvh-1rem)] w-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-t-2xl rounded-b-none border-white/15 bg-black/90 p-0 pb-[env(safe-area-inset-bottom)] text-white shadow-2xl sm:left-auto sm:bottom-auto sm:top-[calc(max(1rem,env(safe-area-inset-top))+4rem)] sm:right-[max(1rem,env(safe-area-inset-right))] sm:max-h-[calc(100dvh-7rem)] sm:w-80 sm:max-w-[calc(100vw-2rem)] sm:rounded-2xl sm:p-0 [--glass-card:rgb(0_0_0/0.9)] [--glass-edge:rgb(255_255_255/0.15)] [--card:0_0%_4%] data-[state=open]:animate-none data-[state=closed]:animate-none motion-reduce:animate-none [&>button:last-child]:right-2 [&>button:last-child]:top-1 [&>button:last-child]:flex [&>button:last-child]:size-11 [&>button:last-child]:items-center [&>button:last-child]:justify-center [&>button:last-child]:rounded-full [&>button:last-child]:text-white"
      >
        <div className="flex min-h-11 shrink-0 items-center border-b border-white/10 px-4 py-3 pr-14">
          <DialogTitle className="text-xs font-medium text-white/65">{hasSiblings ? "Floors and models" : "Tour options"}</DialogTitle>
          <DialogDescription className="sr-only">{hasSiblings ? "Choose a view in this project or read the tour details." : "Information and available options for this tour."}</DialogDescription>
        </div>
        <div className="min-h-0 overflow-y-auto overscroll-contain">
          <div className="border-b border-white/10 px-4 py-4">
            <p className="text-base font-semibold leading-snug text-white [overflow-wrap:anywhere]">{vsName}</p>
          </div>
          {session && <div className="flex flex-wrap gap-2 border-b border-white/10 p-3">
            {session.canResume && <button type="button" disabled={session.busy} onClick={() => { onOpenChange(false); session.resume(); }} className="min-h-11 rounded-xl bg-white px-4 text-sm font-medium text-black hover:bg-white/90 disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white">Resume</button>}
            <button type="button" disabled={!session.canStartOver || session.busy} onClick={() => { onOpenChange(false); session.startOver(); }} className="min-h-11 rounded-xl border border-white/30 px-4 text-sm text-white hover:bg-white/10 disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-white">Start over</button>
          </div>}
          {hasSiblings && <>
          <div className="flex shrink-0 items-center gap-2 border-b border-white/10 px-3 py-2">
            <Search className="size-4 shrink-0 text-white/60" aria-hidden="true" />
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => navigateResults(event, true)}
              aria-label="Search floors and models"
              placeholder="Search floors and models"
              className="min-h-11 min-w-0 flex-1 rounded-md bg-transparent px-1 text-base text-white placeholder:text-white/55 focus-visible:outline-2 focus-visible:outline-white sm:text-sm"
            />
            {query ? <button type="button" aria-label="Clear search" onClick={() => { setQuery(""); searchRef.current?.focus(); }} className="flex size-11 shrink-0 items-center justify-center rounded-full hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-white"><X className="size-4" /></button> : null}
          </div>
          <ul ref={listRef} aria-label="Available floors and models" onKeyDown={(event) => navigateResults(event)} className="py-1">
            {sections
              ? sections.map((section) => (
                  <li key={section.key} role="group" aria-label={section.label || undefined}>
                    {section.label ? (
                      <p className="sticky top-0 z-[1] bg-black/95 px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-white/60">
                        {section.label}
                        <span className="ml-1.5 font-normal normal-case tabular-nums text-white/45">{section.members.length}</span>
                      </p>
                    ) : null}
                    <ul>{section.members.map((s) => renderSibling(s))}</ul>
                  </li>
                ))
              : filteredSiblings.map((s) => renderSibling(s))}
          </ul>
          {filteredSiblings.length === 0 ? <p role="status" className="overflow-y-auto break-words px-4 py-6 text-sm text-white/70">No floors or models match “{query.trim()}”.</p> : null}
          </>}
          {(parentProjectHref || projectPageUrl) ? (
            <a
              href={parentProjectHref || projectPageUrl!}
              className="flex min-h-11 shrink-0 items-center px-4 py-2 text-xs text-white/75 hover:text-white hover:bg-white/10 border-t border-white/10 transition-colors focus-visible:outline-2 focus-visible:outline-inset focus-visible:outline-white"
            >
              Back to project
            </a>
          ) : null}
          <section aria-label="Tour credits" className="border-t border-white/10 px-4 py-3">
            {(organizationLogoUrl || organizationName || customerLogoUrl || customerName) ? (
              <dl className="space-y-3 pb-2">
                <TourCredit label="Tour by" name={organizationName} logoUrl={organizationLogoUrl} fallbackName="Tour provider" />
                <TourCredit label="Prepared for" name={customerName} logoUrl={customerLogoUrl} fallbackName="Customer" />
              </dl>
            ) : null}
            <VirtualSpaceAttribution className="text-white" />
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TourCredit({ label, name, logoUrl, fallbackName }: {
  label: string;
  name?: string | null;
  logoUrl?: string | null;
  fallbackName: string;
}) {
  if (!name && !logoUrl) return null;
  return (
    <div>
      <dt className="text-[11px] text-white/55">{label}</dt>
      <dd className="mt-1 flex min-w-0 items-center gap-2 text-sm text-white/85">
        {logoUrl ? (
          <PublicBrandImage src={logoUrl} name={name || fallbackName} className="max-h-10 w-auto max-w-24 shrink-0" />
        ) : null}
        <span className="min-w-0 [overflow-wrap:anywhere]">{name || fallbackName}</span>
      </dd>
    </div>
  );
}
